import {
  GET_NOTIFICATIONS_TYPES_SUCCESS,
  GET_NOTIFICATIONS_CHANNEL_SUCCESS,
  CREATE_NOTIFICATIONS_CHANNEL_SUCCESS,
  UPDATE_NOTIFICATIONS_CHANNEL_SUCCESS,
  GET_NOTIFICATIONS_SETTINGS_SUCCESS,
  CREATE_NOTIFICATIONS_SETTINGS_REQUEST,
  UPDATE_NOTIFICATIONS_SETTINGS_REQUEST,
  CREATE_NOTIFICATIONS_SETTINGS_FAILURE,
  UPDATE_NOTIFICATIONS_SETTINGS_FAILURE,
  CREATE_NOTIFICATIONS_SETTINGS_SUCCESS,
  UPDATE_NOTIFICATIONS_SETTINGS_SUCCESS,
  DELETE_NOTIFICATIONS_SETTINGS_SUCCESS,
  UPDATE_NOTIFICATIONS_MANAGER,
  LOGOUT_COMPLETE,
  UPDATE_NOTIFICATIONS_CHANNEL_REQUEST,
  UPDATE_NOTIFICATIONS_CHANNEL_FAILURE,
} from '../../constants/actionTypes';
import {
  reduce,
  has,
  cloneDeep,
  sortBy,
  groupBy,
  map,
  mapValues,
  filter,
  forEach,
  merge,
  pull,
} from 'lodash';
import { SOURCE, DATASET, USER } from '../../constants/notificationsTypes';
import { APP, EMAIL, SLACK, WEBHOOKS } from '../../constants/notificationChannels';

const initialState = {
  byCategory: {
    PLATFORM: [],
    SYSTEM: [],
    DATA: [],
  },
  categories: [
    {
      category: 'PLATFORM',
      name: 'Platform Activity',
    },
    {
      category: 'SYSTEM',
      name: 'System Alerts',
    },
    {
      category: 'DATA',
      name: 'Data Alerts',
    },
  ],
  notification_types: null,
  notification_channels_enum: [APP, EMAIL, SLACK, WEBHOOKS],
  notification_channels_enum_map: null,
  notification_channels: null,
  notification_settings: {},
  notificationSettingsByResourceType: {
    [SOURCE]: {},
    [DATASET]: {},
  },
  notificationSettingsBeingSaved: [],
};

export const notificationsManagerReducer = (oldState = initialState, action) => {
  let state = oldState;
  if (
    [
      UPDATE_NOTIFICATIONS_CHANNEL_SUCCESS,
      UPDATE_NOTIFICATIONS_CHANNEL_REQUEST,
      UPDATE_NOTIFICATIONS_CHANNEL_FAILURE,
    ].includes(action.type)
  ) {
    state = Object.assign(oldState, { channelActionType: action.type });
  }
  switch (action.type) {
    case GET_NOTIFICATIONS_TYPES_SUCCESS:
      return setNotificationTypes(state, action);

    case GET_NOTIFICATIONS_CHANNEL_SUCCESS:
      return setNotificationChannels(state, action);

    case CREATE_NOTIFICATIONS_CHANNEL_SUCCESS:
    case UPDATE_NOTIFICATIONS_CHANNEL_SUCCESS:
      return createNotificationChannel(state, action);

    case GET_NOTIFICATIONS_SETTINGS_SUCCESS:
      return setNotificationSettings(state, action);

    case UPDATE_NOTIFICATIONS_MANAGER:
      return updateData(state, action);

    case CREATE_NOTIFICATIONS_SETTINGS_REQUEST:
    case UPDATE_NOTIFICATIONS_SETTINGS_REQUEST:
      return setNotificationSettingBeingSaved(state, action);

    case CREATE_NOTIFICATIONS_SETTINGS_FAILURE:
    case UPDATE_NOTIFICATIONS_SETTINGS_FAILURE:
      return unsetNotificationSettingBeingSaved(state, action);

    case CREATE_NOTIFICATIONS_SETTINGS_SUCCESS:
    case UPDATE_NOTIFICATIONS_SETTINGS_SUCCESS:
      return setNotificationSetting(state, action);

    case DELETE_NOTIFICATIONS_SETTINGS_SUCCESS:
      return deleteResourceNotificationSetting(state, action);

    case LOGOUT_COMPLETE:
      return initialState;

    default:
      return state;
  }
};

export default notificationsManagerReducer;

function updateData(state, action) {
  let n = state.items[action.id];
  n[action.property] = action.value;
  return {
    ...state,
    items: {
      ...state.items,
      [n.id]: n,
    },
  };
}

function setNotificationTypes(state, action) {
  let byCategory = reduce(
    action.result,
    (bc, ntID) => {
      let nt = action.entities.notificationTypes[ntID];
      return {
        ...bc,
        [nt.category]: has(bc, nt.category) ? [...bc[nt.category], ntID] : [ntID],
      };
    },
    {},
  );
  return {
    ...state,
    notification_types: action.entities.notificationTypes,
    byCategory,
  };
}

function setNotificationChannels(state, action) {
  let notification_channels_enum_map = reduce(
    action.result,
    (channMap, channID) => {
      let c = action.entities.notificationChannels[channID];
      return {
        ...channMap,
        [c.channel]: channID,
      };
    },
    {},
  );
  return {
    ...state,
    notification_channels: action.entities.notificationChannels,
    notification_channels_enum_map,
  };
}

function createNotificationChannel(state, action) {
  let notification_channels_enum_map = {
    ...state.notification_channels_enum_map,
  };
  let c = action.entities.notificationChannels[action.result];
  notification_channels_enum_map[c.channel] = c.id;

  return {
    ...state,
    notification_channels_enum_map,
    notification_channels: {
      ...state.notification_channels,
      [c.id]: c,
    },
  };
}

function setNotificationSettings(state, action) {
  const {
    requestInfo: { resourceType },
  } = action;

  let newNotificationSettings = null;

  if (resourceType === USER) {
    newNotificationSettings = reduce(
      action.result,
      (settingsMap, id) => {
        let s = action.entities.notificationSettings[id];
        return {
          ...settingsMap,
          [s.notification_type_id]: settingsMap[s.notification_type_id]
            ? {
                ...settingsMap[s.notification_type_id],
                [s.channel]: {
                  ...s,
                },
              }
            : {
                [s.channel]: {
                  ...s,
                },
              },
        };
      },
      {},
    );
  }

  // [type][resource_id] = sortedSettingIds
  let notificationSettingsByResourceType = {
    ...state.notificationSettingsByResourceType,
  };

  // [type] = settings array
  let newNotificationSettingsByResourceType = groupBy(
    map(action.result, (id) => action.entities.notificationSettings[id]),
    'notification_resource_type',
  );

  // [type][resource_id] = this resource's sorted setting ids
  forEach(newNotificationSettingsByResourceType, (settings, type) => {
    newNotificationSettingsByResourceType[type] = mapValues(
      groupBy(settings, 'resource_id'),
      (values) => map(sortBy(values, [(setting) => -setting.id]), 'id'),
    );
  });

  merge(notificationSettingsByResourceType, newNotificationSettingsByResourceType);

  if (newNotificationSettings) {
    return {
      ...state,
      notification_settings: newNotificationSettings,
      notificationSettingsByResourceType,
    };
  } else {
    return {
      ...state,
      notificationSettingsByResourceType,
    };
  }
}

function setNotificationSettingBeingSaved(state, action) {
  let {
    requestInfo: { typeId, channel },
  } = action;
  let { notificationSettingsBeingSaved } = state;

  return {
    ...state,
    notificationSettingsBeingSaved: [...notificationSettingsBeingSaved, { typeId, channel }],
  };
}

function unsetNotificationSettingBeingSaved(state, action) {
  let {
    requestInfo: { typeId, channel },
  } = action;
  let { notificationSettingsBeingSaved } = state;

  return {
    ...state,
    notificationSettingsBeingSaved: filter(
      notificationSettingsBeingSaved,
      (setting) => setting.typeId !== typeId && setting.channel !== channel,
    ),
  };
}

function setNotificationSetting(state, action) {
  let {
    entities: { notificationSettings },
    result,
  } = action;
  let notification_settings = cloneDeep(state.notification_settings);
  let s = notificationSettings[result];
  if (!notification_settings[s.notification_type_id]) {
    notification_settings[s.notification_type_id] = {};
  }
  notification_settings[s.notification_type_id][s.channel] = s;
  let newState = unsetNotificationSettingBeingSaved(state, action);
  return {
    ...newState,
    notification_settings,
  };
}

function deleteResourceNotificationSetting(state, action) {
  const {
    requestInfo: { id, resourceType, resourceId },
  } = action;
  if (resourceType && resourceId) {
    //need resource type and resource id

    let newSettingsForThisResource = has(
      state.notificationSettingsByResourceType,
      `${resourceType}.${resourceId}`,
    )
      ? pull(state.notificationSettingsByResourceType[resourceType][resourceId], id)
      : [];

    let notificationsForThisResourceType = {
      ...state.notificationSettingsByResourceType[resourceType],
      [resourceId]: newSettingsForThisResource,
    };

    let notificationSettingsByResourceType = {
      ...state.notificationSettingsByResourceType,
      [resourceType]: notificationsForThisResourceType,
    };

    return { ...state, notificationSettingsByResourceType };
  } else {
    return state;
  }
}
